import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Utensils, ArrowLeft, Mail, Check, Clock, Trash2,
  Users, Calendar, MapPin, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { getExpense, deleteExpense, sendSplitRequest, createSettlement } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  GBP: '£',
  EUR: '€',
  AUD: 'A$',
  CAD: 'C$'
};

const ExpenseDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [settlingId, setSettlingId] = useState(null);

  useEffect(() => {
    fetchExpense();
  }, [id]);

  const fetchExpense = async () => {
    try {
      const res = await getExpense(id);
      setExpense(res.data);
    } catch (error) {
      toast.error('Failed to load expense');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const currency = expense?.currency || 'INR';
  const currencySymbol = CURRENCY_SYMBOLS[currency] || '₹';

  const formatCurrency = (amount) => {
    return `${currencySymbol}${amount?.toFixed(2) || '0.00'}`;
  };

  const handleDelete = async () => {
    try {
      await deleteExpense(id);
      toast.success('Expense deleted');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const handleSendReminder = async (email) => {
    setSending(true);
    try {
      await sendSplitRequest({
        expense_id: id,
        recipient_emails: [email]
      });
      toast.success(`Reminder sent to ${email}`);
    } catch (error) {
      toast.error('Failed to send reminder');
    } finally {
      setSending(false);
    }
  };

  const handleMarkPaid = async (split) => {
    // Use email as identifier since it's always available, user_id may be null for guests
    const fromUserId = split.email || split.user_id;
    setSettlingId(fromUserId);
    try {
      await createSettlement({
        expense_id: id,
        from_user_id: fromUserId,
        to_user_id: user.user_id,
        amount: split.amount
      });
      toast.success(`Marked ${split.name} as paid`);
      fetchExpense();
    } catch (error) {
      toast.error('Failed to update payment status');
    } finally {
      setSettlingId(null);
    }
  };

  const isOwner = expense?.created_by === user?.user_id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fff7ed 100%)'}}>
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!expense) return null;

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fff7ed 100%)'}}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-mesh pointer-events-none" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-xl border-b border-orange-100 z-40 flex items-center px-4 lg:px-8 shadow-sm">
        <div className="max-w-4xl mx-auto w-full flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-orange-50 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <h1 className="text-xl font-semibold font-['Poppins'] truncate text-slate-800">{expense.title}</h1>
          </div>
          
          {isOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-50" data-testid="delete-expense-btn">
                  <Trash2 className="w-5 h-5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white border-slate-200">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-slate-800">Delete Expense?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-500">
                    This will permanently delete this expense and all associated data. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-slate-200 hover:bg-slate-50">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 px-4 lg:px-8 pb-8 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Expense Overview */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/90 backdrop-blur rounded-3xl p-8 mb-6 border border-orange-100 shadow-lg"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                    <Utensils className="w-7 h-7 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold font-['Poppins'] text-slate-800">{expense.title}</h2>
                    {expense.merchant && (
                      <p className="text-slate-500 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {expense.merchant}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <p className="text-4xl font-bold text-gradient">{formatCurrency(expense.total_amount)}</p>
                <p className="text-slate-500 flex items-center justify-end gap-1 mt-1">
                  <Calendar className="w-4 h-4" />
                  {expense.date} {expense.time && `at ${expense.time}`}
                </p>
              </div>
            </div>
            
            {/* Meta info */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 border border-orange-100">
                <Users className="w-4 h-4 text-orange-600" />
                <span className="text-sm text-slate-700">{expense.participants?.length || 0} people</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-100">
                <span className="text-sm text-slate-700 capitalize">{expense.split_type?.replace('_', ' ')} split</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100">
                <span className="text-sm text-slate-700">{currency}</span>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                expense.status === 'completed' ? 'bg-green-50 border-green-200 text-green-600' : 'bg-amber-50 border-amber-200 text-amber-600'
              }`}>
                {expense.status === 'completed' ? <Check className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                <span className="text-sm font-medium capitalize">{expense.status}</span>
              </div>
            </div>
          </motion.div>

          {/* Items */}
          {expense.items?.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/90 backdrop-blur rounded-2xl p-6 mb-6 border border-orange-100 shadow-lg"
            >
              <h3 className="text-lg font-semibold font-['Poppins'] mb-4 text-slate-800">Items</h3>
              <div className="space-y-3">
                {expense.items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                    <div>
                      <span className="text-slate-800">{item.name}</span>
                      {item.quantity > 1 && <span className="text-slate-400 ml-2">x{item.quantity}</span>}
                    </div>
                    <span className="text-slate-600">{formatCurrency(item.price * (item.quantity || 1))}</span>
                  </div>
                ))}
              </div>
              
              {/* Taxes & Tip */}
              {(expense.cgst > 0 || expense.sgst > 0 || expense.tip > 0) && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                  {expense.cgst > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>CGST</span>
                      <span>{formatCurrency(expense.cgst)}</span>
                    </div>
                  )}
                  {expense.sgst > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>SGST</span>
                      <span>{formatCurrency(expense.sgst)}</span>
                    </div>
                  )}
                  {expense.tip > 0 && (
                    <div className="flex justify-between text-slate-600">
                      <span>Tip / Service Charge</span>
                      <span>{formatCurrency(expense.tip)}</span>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Splits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-orange-100 shadow-lg"
          >
            <h3 className="text-lg font-semibold font-['Poppins'] mb-4 text-slate-800">Split Breakdown</h3>
            <div className="space-y-4">
              {expense.splits?.map((split, index) => {
                const isCurrentUser = split.user_id === user?.user_id;
                const isPaid = split.paid || isCurrentUser;
                
                return (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-4 rounded-xl ${
                      isPaid ? 'bg-green-50 border border-green-100' : 'bg-slate-50'
                    }`}
                    data-testid={`split-row-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium shadow-md ${
                        isCurrentUser 
                          ? 'bg-gradient-to-br from-orange-500 to-red-500' 
                          : 'bg-gradient-to-br from-green-500 to-emerald-500'
                      }`}>
                        {split.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          {split.name || split.email}
                          {isCurrentUser && <span className="text-slate-400 ml-2">(You - Paid)</span>}
                        </p>
                        <p className="text-sm text-slate-500">{split.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className={`text-lg font-semibold ${
                        isPaid ? 'text-green-600' : 'text-slate-800'
                      }`}>
                        {formatCurrency(split.amount)}
                      </span>
                      
                      {!isCurrentUser && isOwner && (
                        <div className="flex items-center gap-2">
                          {!isPaid && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSendReminder(split.email)}
                                disabled={sending}
                                className="text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                                data-testid={`remind-${index}-btn`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleMarkPaid(split)}
                                disabled={settlingId === split.email}
                                className="bg-green-100 text-green-600 hover:bg-green-200"
                                data-testid={`mark-paid-${index}-btn`}
                              >
                                <Check className="w-4 h-4 mr-1" />
                                Mark Paid
                              </Button>
                            </>
                          )}
                          {isPaid && (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <Check className="w-4 h-4" />
                              Paid
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Notes */}
          {expense.notes && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white/90 backdrop-blur rounded-2xl p-6 mt-6 border border-orange-100 shadow-lg"
            >
              <h3 className="text-lg font-semibold font-['Poppins'] mb-2 text-slate-800">Notes</h3>
              <p className="text-slate-600">{expense.notes}</p>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ExpenseDetail;
